import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import * as symbolUtils from "@arcgis/core/symbols/support/symbolUtils.js";
import Draw from "@arcgis/core/views/draw/Draw";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { OverettContext } from "../../../Context";
import { setOverettCreationStep } from "../../../store/overettSlice";
import { applyEdits, getMapView } from "../../../utils/arcgisUtils";
import { NFSStatus } from "../../../utils/nfsUtils";
import { getOverettElevations } from "../../../utils/overettUtils";
import Loader from "../../genericComponents/Loader/Loader";
import { OverettCreationStep } from "./OverettEditorContent";
import "./OverettPanel.css";
import { NauticalClasses } from "../../../constants/NauticalObjects";
import { Navlystype } from "../../../constants/nfsDomains";

const OverettLinePanel = () => {
    const dispatch = useDispatch();
    const overettContext = useContext(OverettContext);
    const overettObjects = overettContext?.overettObjects.value;
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const upperOverettRef = useRef<Graphic | null>(null);

    const overettLineSymbol = useRef<SimpleLineSymbol>(
        new SimpleLineSymbol({
            color: [255, 0, 0, 1],
            width: 2,
            style: "solid",
        })
    );
    const overettLinjeDrawRef = useRef<Draw | null>(null);

    const drawLine = async (event: any) => {
        if (!overettObjects) return;
        const mapView = getMapView();
        if (!mapView?.map) return;

        const overettLinjeLayer = mapView.map.findLayerById("OverettlinjeEditing") as GraphicsLayer;
        if (!overettLinjeLayer) return;

        overettLinjeLayer.removeAll();

        const featureCenter = upperOverettRef.current?.geometry as Point;

        const polyline = new Polyline({
            paths: [
                [
                    [featureCenter.x, featureCenter.y],
                    [event.vertices[0][0], event.vertices[0][1]],
                ],
            ],
            spatialReference: upperOverettRef.current?.geometry?.spatialReference,
        });

        const graphic = new Graphic({
            geometry: polyline,
            symbol: overettLineSymbol.current,
        });

        overettLinjeLayer.add(graphic);

        return graphic;
    };

    useEffect(() => {
        if (!overettObjects) return;
        const mapView = getMapView();
        if (!mapView?.map) return;

        const featureLayer = overettObjects.upper?.layer as FeatureLayer;

        const overettEditingLayer = mapView.map.findLayerById("OverettlinjeEditing") as GraphicsLayer;
        const overettLinjeLysLayer = mapView.map.findLayerById("Overettlinje lys") as FeatureLayer;
        const overettLinjeMerkeLayer = mapView.map.findLayerById("Overettlinje merke") as FeatureLayer;
        const overettLinjeLayer = featureLayer.id === "Lys" ? overettLinjeLysLayer : overettLinjeMerkeLayer;

        const dummyLine = new Graphic({
            layer: overettLinjeLayer,
        });
        symbolUtils.getDisplayedSymbol(dummyLine).then((symbol) => {
            overettLineSymbol.current = symbol as SimpleLineSymbol;
        });

        const draw = new Draw({
            view: mapView,
        });
        overettLinjeDrawRef.current = draw;

        const action = overettLinjeDrawRef.current.create("polyline", { mode: "click" });

        setIsLoading(true);

        if (!overettObjects.upper || !overettObjects.lower) {
            setIsLoading(false);
            return;
        }

        getOverettElevations(overettObjects?.upper, overettObjects?.lower).then((results) => {
            if (!results) return;
            const [upperOverett] = results;
            upperOverettRef.current = upperOverett;
            const featureCenter = upperOverett.geometry as Point;
            drawLine({ vertices: [[featureCenter.x, featureCenter.y]] });
            setIsLoading(false);
        });

        const actionCursorUpdate = action.on("cursor-update", (event) => {
            if (event.vertices.length > 1) return;
            drawLine(event);
        });

        const actionVertexAdd = action.on("vertex-add", async (event) => {
            const graphic = await drawLine(event);
            overettLinjeDrawRef.current?.complete();

            if (!graphic) return;

            graphic.attributes = {
                fknavinst1: overettObjects?.upper?.attributes.globalid,
                fknavinst2: overettObjects?.lower?.attributes.globalid,
                nfs_status: NFSStatus.PROJECTED,
            };

            const newLine = await applyEdits(overettLinjeLayer, {
                addFeatures: [graphic],
            });
            const addedLine = newLine.addFeatureResults[0];
            graphic.attributes.objectid = addedLine.objectId;
            graphic.attributes.globalid = addedLine.globalId;
            await applyEdits(overettLinjeLayer, {
                updateFeatures: [graphic],
            });

            const overettLayer = overettObjects.upper?.layer as FeatureLayer;

            if (
                overettObjects.upper?.attributes.klasse === NauticalClasses.LA_Lanterne ||
                overettObjects.upper?.attributes.klasse === NauticalClasses.HIB_HurtigbaatmerkeMedIndirekteBelysning
            ) {
                overettObjects.upper.attributes.lys_type = Navlystype.Overettlys;
            }
            if (
                overettObjects.lower?.attributes.klasse === NauticalClasses.LA_Lanterne ||
                overettObjects.lower?.attributes.klasse === NauticalClasses.HIB_HurtigbaatmerkeMedIndirekteBelysning
            ) {
                overettObjects.lower.attributes.lys_type = Navlystype.Overettlys;
            }

            await applyEdits(overettLayer, {
                updateFeatures: [overettObjects.upper, overettObjects.lower].filter(Boolean) as Graphic[],
            });

            overettLinjeLayer.visible = false;
            overettContext.overettLinjeRef.value.current = graphic;
            overettContext.overettLinjeRef.value.current.layer = overettLinjeLayer;
            overettEditingLayer?.graphics?.removeAll();
            dispatch(setOverettCreationStep(OverettCreationStep.EditOverett));
        });

        return () => {
            actionCursorUpdate.remove();
            actionVertexAdd.remove();
        };
    }, []);

    return (
        <>
            <div className="overett-panel">Trekk overettlinje mellom de to objektene.</div>
            {isLoading && <Loader overlay="fullscreen" />}
        </>
    );
};

export default OverettLinePanel;
